/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useContext, useRef, useEffect } from 'react';
import Modal from '@mui/material/Modal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { useLang } from '@spa/i18n/useLang';
import { CompanyContext } from '@spa/providers/CompanyContext';
import CartClearConfirmDialog from '@spa/components/Header/CartClearConfirmDialog';
import BasicLoader from '@spa/components/BasicLoader';
import { useUser } from '@spa/hooks/useUser';
import Searchbar from '@spa/components/Header/SearchBar';
import CompanyPickerListContent from '@spa/components/Header/CompanyPickerListContent';
import { CartContext } from '@extensions/providers/cartProvider';
import type { Company } from '@spa/types/companies.types';
import BasicSpinner from '@spa/components/BasicSpinner';
import { COMPANY_PICKER_SHAKE_EVENT } from '@spa/events/companyPickerEvents';

function CompanyPicker() {
  const { isSuperAdmin } = useUser();
  const t = useLang();
  const { cart } = useContext(CartContext);

  const {
    companies,
    activeCompany,
    ownCompanyId,
    searchUserCompanies,
    impersonateCompany,
    endCompanyImpersonation,
    isLoadingCompanies,
    companyPickerEnabled,
    activeChannelName,
  } = useContext(CompanyContext);

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [shake, setShake] = useState(false);
  const [showPickerMessage, setShowPickerMessage] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => searchUserCompanies(searchQuery), [searchQuery]);

  useEffect(() => {
    const handleShake = () => {
      pickerRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      setShake(true);
      setShowPickerMessage(true);
      setTimeout(() => setShake(false), 600);

      // Reset dismiss timer on every shake
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = setTimeout(
        () => setShowPickerMessage(false),
        3000
      );
    };

    window.addEventListener(COMPANY_PICKER_SHAKE_EVENT, handleShake);
    return () => {
      window.removeEventListener(COMPANY_PICKER_SHAKE_EVENT, handleShake);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, []);

  const [pendingAction, setPendingAction] = useState<
    (() => Promise<void>) | null
  >(null);

  const hasSingleCompany = companies.length === 1;
  const isPickerDisabled = !companyPickerEnabled || hasSingleCompany;

  let activeCompanyName = activeCompany?.company.companyName;
  activeCompanyName =
    !activeCompanyName && isSuperAdmin
      ? t('global.superAdmin')
      : activeCompanyName;

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleOpen = () => {
    setIsOpen(true);
    setSearchQuery('');
    setShowPickerMessage(false);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
  };

  const handleClose = () => {
    setIsOpen(false);
    searchUserCompanies('');
  };

  const wasLoadingRef = useRef(false);
  const selectInitiatedRef = useRef(false);
  useEffect(() => {
    if (isLoadingCompanies) {
      wasLoadingRef.current = true;
    }

    if (
      !isLoadingCompanies &&
      wasLoadingRef.current &&
      selectInitiatedRef.current
    ) {
      wasLoadingRef.current = false;
      selectInitiatedRef.current = false;

      if (isOpen) {
        setTimeout(() => handleClose(), 0);
      }
    }
  }, [isLoadingCompanies]);

  const handleSelectCompany = (company: Company) => {
    selectInitiatedRef.current = true;
    setShowPickerMessage(false);
    if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);

    const isSwitching = !!activeCompany;
    const hasActiveCart = (cart?.lineItems?.digitalItems?.length ?? 0) > 0;

    if (isSwitching && hasActiveCart) {
      setPendingAction(() => async () => {
        await impersonateCompany(company);
      });
    } else {
      impersonateCompany(company);
    }
  };

  const handleDialogConfirm = async () => {
    if (pendingAction) {
      await pendingAction();
    }
    setPendingAction(null);
  };

  const handleDialogCancel = () => {
    setPendingAction(null);
  };

  return (
    <div ref={pickerRef} className="relative flex flex-col gap-1">
      <div
        className={`flex items-center gap-2 ${shake ? 'animate-shake' : ''}`}
      >
        <button
          onClick={handleOpen}
          disabled={isPickerDisabled}
          className="
            flex items-center gap-3
            px-4 py-2.5
            border-2 border-primary-text!
            rounded bg-white
            text-primary-text text-sm font-medium
            hover:bg-brand-hover
            transition-colors
            w-full
            min-w-0 overflow-hidden
            disabled:opacity-50 disabled:cursor-not-allowed
          "
        >
          <span className="flex flex-col items-start min-w-0 flex-1 overflow-hidden">
            <span className="text-xs text-primary-text opacity-70 leading-tight">
              {activeChannelName}
            </span>
            <span className="truncate leading-tight pt-1">
              {isLoadingCompanies && !activeCompanyName
                ? t('companyPicker.loading')
                : activeCompanyName}
            </span>
          </span>
          {isLoadingCompanies ? (
            <BasicSpinner />
          ) : (
            <KeyboardArrowDownIcon className="shrink-0" fontSize="small" />
          )}
        </button>
      </div>

      {showPickerMessage && (
        <div className="absolute top-full left-0 mt-2 z-50 bg-primary-text text-negative-text font-semibold! text-xs px-3 py-2 rounded shadow-lg">
          <div className="absolute -top-1 left-4 w-2 h-2 bg-primary-text rotate-45" />
          {t('companyPicker.impersonationRequired.message')}
        </div>
      )}

      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="company-picker-title"
      >
        <div className="fixed inset-0 flex items-center justify-center p-0 sm:p-4">
          <div
            className="
              bg-modal-background rounded-lg shadow-2xl
              w-full max-w-full sm:max-w-[70vw]
              max-h-[91vh] flex flex-col
              outline-none text-primary-text
              p-8! sm:p-8 relative
            "
          >
            {isLoadingCompanies && (
              <div className="absolute inset-0 z-10 flex justify-center items-center bg-modal-background/80 rounded-lg">
                <BasicLoader />
              </div>
            )}

            <div className="flex items-center justify-between pb-3">
              <h2
                id="company-picker-title"
                className="text-2xl font-bold m-0 text-primary-text"
              >
                {t('companyPicker.dialog.title')}
              </h2>
              <button onClick={handleClose} aria-label="close">
                <CloseIcon sx={{ fontSize: 24 }} />
              </button>
            </div>

            <Searchbar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('companyPicker.search.placeholder')}
              className="mb-4"
            />

            <CompanyPickerListContent
              companies={companies}
              scrollContainerRef={scrollContainerRef}
              isOpen={isOpen}
              isLoading={isLoadingCompanies}
              isSuperAdmin={isSuperAdmin}
              ownCompanyId={ownCompanyId}
              activeCompanyId={activeCompany?.company?.companyId}
              onSelectCompany={handleSelectCompany}
              onDeselectCompany={() => {
                setPendingAction(null);
                selectInitiatedRef.current = true;
                endCompanyImpersonation();
              }}
            />
          </div>
        </div>
      </Modal>

      <CartClearConfirmDialog
        open={!!pendingAction}
        isLoading={isLoadingCompanies}
        onConfirm={handleDialogConfirm}
        onCancel={handleDialogCancel}
      />
    </div>
  );
}

export default CompanyPicker;
